import type { GenericField, GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { PhoneInput } from '@neo/primitives/country-dropdown-input'
import { Label } from '@neo/primitives/label'
import { PlusIcon, X } from 'lucide-react'
import { v4 } from 'uuid'
import type { FileMimeType, RFileUploadResponse } from 'v2/types/api/auth'
import z from 'zod'
import { MediaUpload } from '../../components/media-upload/media-upload'
import { NodevarList } from '../../components/nodevar-list'
import { v2validations } from '../../config/validation.config'
import type { AgentPresetConversationNodeformData } from '../../types'

export const chatSpecificFields = (
  nodeVars: string[],
): GenericField<AgentPresetConversationNodeformData>[] => {
  const validationSchema = v2validations({ nodeVars }).agent_preset_conversation.innerType()

  return [
    {
      name: 'content_type',
      label: 'Message type',
      componentType: 'select',
      zodValidation: validationSchema.shape.content_type,
      onChangeListenTo: ['channel'],
      visible: (values: AgentPresetConversationNodeformData) => values.channel === 'chat',
      inputProps: {
        searchable: false,
        options: [
          { label: 'Text', value: 'text' },
          { label: 'Image/Audio/Video/Document', value: 'chat_media' },
          { label: 'Message with buttons', value: 'chat_button' },
        ],
      },
      afterOnChange: (values: AgentPresetConversationNodeformData, _, form) => {
        if (values.content_type === 'chat_button') {
          if (!values.buttons || values.buttons.length === 0) {
            form?.setFieldValue('buttons', [
              {
                id: v4(),
                button_type: 'reply',
                text: 'Button 1',
              },
            ])
          }
          form?.setFieldValue('wait_for_response', true)
          form?.setFieldValue('reply_timeout', 3600)
        } else {
          form?.setFieldValue('buttons', [])
          form?.setFieldValue('header_media', undefined)
          form?.setFieldValue('title', '')
          form?.setFieldValue('button_description', '')
        }
      },
    },
    {
      name: 'chat_media',
      type: 'custom',
      label: 'Upload media',
      zodValidation: validationSchema.shape.chat_media,
      onChangeListenTo: ['content_type', 'channel'],
      visible: (values: AgentPresetConversationNodeformData) =>
        values.channel === 'chat' && values.content_type === 'chat_media',
      eventDataExtractor: (data: Record<string, unknown>) => data,
      component: ({
        form,
        onChange,
      }: {
        form: GenericFormType<AgentPresetConversationNodeformData>
        onChange: (value: unknown) => void
      }) => {
        const onURLChange = (uploadedFile: RFileUploadResponse) => {
          const mediaFile = {
            media_type: uploadedFile.file_type,
            name: uploadedFile.file_name,
            url: uploadedFile.file_url,
            size: uploadedFile.file_size,
            file_id: uploadedFile.file_id,
          }
          onChange(mediaFile)
        }

        const onRemove = () => {
          onChange(undefined)
        }

        let initialFiles: RFileUploadResponse[] = []
        if (form.state.values.chat_media && form.state.values.chat_media.url) {
          initialFiles = [
            {
              file_type: form.state.values.chat_media.media_type as FileMimeType,
              file_name: form.state.values.chat_media.name || '',
              file_url: form.state.values.chat_media.url,
              file_size: form.state.values.chat_media.size || 0,
              file_id: form.state.values.chat_media.file_id || '',
            },
          ]
        }

        return (
          <MediaUpload
            onURLChange={onURLChange}
            label="Upload media"
            initialFiles={initialFiles}
            multiUpload={false}
            onRemove={onRemove}
            nodeVars={nodeVars}
          />
        )
      },
    },
    // Chat button title and button_description fields
    {
      name: 'title',
      label: 'Title',
      componentType: 'input',
      zodValidation: (values: AgentPresetConversationNodeformData) => {
        if (values.channel === 'chat' && values.content_type === 'chat_button') {
          return validationSchema.shape.title.refine((title) => (title?.length || 0) > 0, {
            message: 'Title is required',
          })
        }
        return validationSchema.shape.title
      },
      onChangeListenTo: ['content_type', 'channel'],
      visible: (values: AgentPresetConversationNodeformData) =>
        values.channel === 'chat' && values.content_type === 'chat_button',
      inputProps: {
        placeholder: 'Enter title',
        id: 'agent-preset-chat-title',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="#agent-preset-chat-title"
          />
        ),
      },
    },
    {
      name: 'button_description',
      label: 'Description',
      componentType: 'textarea',
      zodValidation: validationSchema.shape.button_description,
      onChangeListenTo: ['content_type', 'channel'],
      visible: (values: AgentPresetConversationNodeformData) =>
        values.channel === 'chat' && values.content_type === 'chat_button',
      inputProps: {
        placeholder: 'Enter description',
        id: 'agent-preset-chat-button-description',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="#agent-preset-chat-button-description"
          />
        ),
      },
    },
    // Chat buttons configuration
    {
      name: 'buttons',
      type: 'array',
      label: (
        <Label className="col-span-full text-s-500 mb-2">
          Configure buttons <i className="text-xsi-400">(max 5 supported)</i>
        </Label>
      ),
      className:
        'flex gap-4 p-4 rounded-md bg-bg2 w-full relative -mt-4 col-span-full flex-wrap mb-2',
      visible: (values: AgentPresetConversationNodeformData) =>
        values.channel === 'chat' && values.content_type === 'chat_button',
      onChangeListenTo: ['content_type', 'channel'],
      defaultNewValue: ({ state }) => ({
        id: v4(),
        button_type: 'reply',
        text: `Button ${(state.values.buttons?.length || 0) + 1}`,
      }),
      zodValidation: (values) => {
        if (values.channel === 'chat' && values.content_type === 'chat_button') {
          return z
            .array(
              z.object({
                id: z.string(),
                button_type: z.enum(['call', 'reply', 'website']),
                text: z.string().max(100, 'Button text cannot exceed 100 characters'),
                call_phone: z.string().optional(),
                website_url: z.string().optional(),
              }),
            )
            .min(1, 'At least one button is required')
            .max(5, 'Maximum 5 buttons allowed')
        }
        return z.any()
      },
      addItemButton: ({ state, ...rest }) => (
        <div className="col-span-full flex justify-start rounded-b-md items-start pb-2">
          <Button
            size="sm"
            variant="ghost"
            type="button"
            className="ml-2 form-button"
            {...rest}
            disabled={(state?.buttons?.length || 0) >= 5}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Add Button
          </Button>
        </div>
      ),
      removeItemButton: <X className="w-4 h-4 absolute top-3 right-4 text-tertiary" />,
      afterOnChange: (values: AgentPresetConversationNodeformData, _, form) => {
        // Prevent removing the last button
        if (values.buttons && values.buttons.length === 0) {
          form?.setFieldValue('buttons', [
            {
              id: v4(),
              button_type: 'reply',
              text: 'Button 1',
            },
          ])
        }
      },
      subFields: [
        {
          name: 'buttons[0].button_type',
          label: 'Button type',
          componentType: 'select',
          className: 'w-full',
          zodValidation: z.enum(['call', 'reply', 'website']),
          inputProps: {
            searchable: false,
            options: [
              { label: 'Call Number', value: 'call' },
              { label: 'Quick Action', value: 'reply' },
              { label: 'Website', value: 'website' },
            ],
          },
          afterOnChange: (values: AgentPresetConversationNodeformData, arrayIndex, form) => {
            const buttonType = values.buttons?.[arrayIndex || 0]?.button_type

            // Clear the type-specific fields when changing button type
            if (buttonType === 'call') {
              form?.setFieldValue(`buttons[${arrayIndex || 0}].website_url`, undefined)
            } else if (buttonType === 'website') {
              form?.setFieldValue(`buttons[${arrayIndex || 0}].call_phone`, undefined)
            } else if (buttonType === 'reply') {
              form?.setFieldValue(`buttons[${arrayIndex || 0}].website_url`, undefined)
              form?.setFieldValue(`buttons[${arrayIndex || 0}].call_phone`, undefined)
            }
          },
        },
        {
          name: 'buttons[0].text',
          label: 'Button text',
          componentType: 'input',
          className: 'w-full',
          zodValidation: z.string().max(100, 'Max 100 characters'),
          inputProps: {
            placeholder: 'Enter button text',
          },
        },
        {
          onChangeListenTo: ['buttons[0].button_type'],
          name: 'buttons[0].call_phone',
          label: 'Phone number',
          type: 'custom',
          className: 'w-full col-span-full mt-2',
          eventDataExtractor: (data: Record<string, unknown>) => data,
          visible: (values: AgentPresetConversationNodeformData, arrayIndex?: number) => {
            const buttonType = values.buttons?.[arrayIndex || 0]?.button_type
            return buttonType === 'call'
          },
          zodValidation: z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number format'),
          component: ({ onChange, value, id, name }) => (
            <PhoneInput
              id={id}
              name={name}
              value={value as string}
              onChange={(newValue) => onChange(newValue)}
              international
              countryCallingCodeEditable
              defaultCountry="US"
            />
          ),
        },
        {
          onChangeListenTo: ['buttons[0].button_type'],
          name: 'buttons[0].website_url',
          label: 'Website URL',
          componentType: 'input',
          className: 'w-full col-span-full mt-2',
          visible: (values: AgentPresetConversationNodeformData, arrayIndex?: number) => {
            const buttonType = values.buttons?.[arrayIndex || 0]?.button_type
            return buttonType === 'website'
          },
          zodValidation: z.string().url('Invalid URL format'),
          inputProps: {
            placeholder: 'Enter URL',
            type: 'url',
          },
        },
      ],
    },
  ]
}
